import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Calendar, Clock, AlertCircle } from 'lucide-react';

interface Config {
  id: number;
  title: string;
  description: string | null;
  slot_duration_minutes: number;
  appointment_type: string;
  public_token: string;
  first_name: string;
  last_name: string;
  available_slots_count: number;
}

export default function AppointmentsAll() {
  const navigate = useNavigate();

  const { data: configs, isLoading, error } = useQuery<Config[]>(
    ['appointments', 'public', 'all'],
    async () => {
      const response = await api.get('/appointments/public/all');
      return response.data.data;
    },
    { retry: 1 }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !configs || configs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-50">Keine Terminbuchungen verfügbar</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Derzeit sind keine Terminbuchungen aktiv.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 text-gray-900 dark:text-gray-50">Terminbuchungen</h1>
          <p className="text-gray-600 dark:text-gray-400">Wählen Sie einen Lehrer aus, um einen Termin zu buchen</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {configs.map(config => (
            <div
              key={config.id}
              onClick={() => navigate(`/appointments/${config.public_token}`)}
              className="card p-6 hover:shadow-lg transition cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold mb-1 text-gray-900 dark:text-gray-50">{config.title}</h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {config.first_name} {config.last_name}
                  </p>
                </div>
              </div>
              
              {config.description && (
                <p className="text-gray-700 dark:text-gray-300 mb-4 text-sm">{config.description}</p>
              )}

              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {config.slot_duration_minutes} Min
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {config.available_slots_count} verfügbar
                </span>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition">
                  Termin buchen
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

